import React, { useEffect, useMemo, useState } from "react";
import {
  assignDojosToTeacher,
  assignGroupsToTeacher,
} from "../../../services/teacherServices";
import { getGroupsByDojo } from "../../../services/groupServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import {
  FaLayerGroup,
  FaMapMarkerAlt,
  FaSave,
  FaTimes,
  FaUsersCog,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const AssignTeacherModal = ({
  isOpen,
  onClose,
  teacher,
  dojos,
  initialGroups = [],
  onSaved,
  refreshTeachers,
  demoMode = false,
}) => {
  const { addNotification } = useNotification();

  const [selectedDojos, setSelectedDojos] = useState([]);
  const [selectedGroupsByDojo, setSelectedGroupsByDojo] = useState({});
  const [groupsByDojo, setGroupsByDojo] = useState({});
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen || !teacher) return;

    const dojoIds = [];
    const groupsMap = {};

    initialGroups.forEach((group) => {
      const dojoId = group?.dojo?.id ?? group?.dojo_id;
      if (!dojoId) return;

      const dojoKey = String(dojoId);

      if (!dojoIds.includes(dojoKey)) {
        dojoIds.push(dojoKey);
      }

      if (!groupsMap[dojoKey]) {
        groupsMap[dojoKey] = [];
      }

      groupsMap[dojoKey].push(group.id);
    });

    setSelectedDojos(dojoIds);
    setSelectedGroupsByDojo(groupsMap);
    setGroupsByDojo({});
    setErrorMessage("");
    setSaving(false);
  }, [isOpen, teacher, initialGroups]);

  useEffect(() => {
    if (!isOpen) return;

    if (selectedDojos.length === 0) {
      setGroupsByDojo({});
      return;
    }

    fetchGroupsForDojos(selectedDojos);
  }, [isOpen, selectedDojos]);

  const fetchGroupsForDojos = async (dojoIds) => {
    try {
      const entries = await Promise.all(
        dojoIds.map(async (dojoId) => {
          const data = await getGroupsByDojo(dojoId);
          return [String(dojoId), Array.isArray(data) ? data : []];
        })
      );

      setGroupsByDojo((prev) => ({
        ...prev,
        ...Object.fromEntries(entries),
      }));
    } catch (error) {
      console.error("Erreur lors du chargement des groupes :", error);
      addNotification("Erreur lors du chargement des groupes.", "error");
    }
  };

  const toggleDojoSelection = (dojoId) => {
    const dojoKey = String(dojoId);

    setSelectedDojos((prev) => {
      const exists = prev.includes(dojoKey);

      if (exists) {
        const next = prev.filter((id) => id !== dojoKey);

        setSelectedGroupsByDojo((current) => {
          const clone = { ...current };
          delete clone[dojoKey];
          return clone;
        });

        return next;
      }

      return [...prev, dojoKey];
    });
  };

  const toggleGroupSelection = (dojoId, groupId) => {
    const dojoKey = String(dojoId);

    setSelectedGroupsByDojo((prev) => {
      const currentGroups = prev[dojoKey] || [];

      if (currentGroups.includes(groupId)) {
        return {
          ...prev,
          [dojoKey]: currentGroups.filter((id) => id !== groupId),
        };
      }

      return {
        ...prev,
        [dojoKey]: [...currentGroups, groupId],
      };
    });
  };

  const totalSelectedGroups = useMemo(() => {
    return Object.values(selectedGroupsByDojo).reduce(
      (acc, groups) => acc + groups.length,
      0
    );
  }, [selectedGroupsByDojo]);

  const handleSave = async () => {
    if (!teacher?.id) return;

    if (demoMode) {
      setErrorMessage(
        "Mode Démo : les affectations ne peuvent pas être enregistrées."
      );
      return;
    }

    try {
      setSaving(true);
      setErrorMessage("");

      const dojoIdsAsNumbers = selectedDojos.map(Number);

      await assignDojosToTeacher(teacher.id, dojoIdsAsNumbers);

      const allSelectedGroups = selectedDojos.flatMap((dojoId) =>
        (selectedGroupsByDojo[dojoId] || []).map((groupId) => ({
          dojo_id: Number(dojoId),
          group_id: Number(groupId),
        }))
      );

      await assignGroupsToTeacher(teacher.id, allSelectedGroups);

      const resolvedGroups = allSelectedGroups.map(({ dojo_id, group_id }) => {
        const group =
          groupsByDojo[String(dojo_id)]?.find((g) => g.id === group_id) || null;

        return {
          id: group_id,
          nom_groupe: group?.nom_groupe || "Groupe inconnu",
          dojo_id: dojo_id,
          dojo: {
            id: dojo_id,
            nom_dojo:
              dojos.find((d) => String(d.id) === String(dojo_id))?.nom_dojo ||
              "Lieu inconnu",
          },
        };
      });

      addNotification("Affectations enregistrées avec succès.", "success");

      if (onSaved) {
        onSaved(resolvedGroups);
      }

      if (refreshTeachers) {
        await refreshTeachers();
      }

      onClose();
    } catch (error) {
      console.error("Erreur lors de l'affectation :", error);
      setErrorMessage("Erreur lors de l'enregistrement des affectations.");
      addNotification("Erreur lors de l'enregistrement des affectations.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[130] bg-slate-950/70 backdrop-blur-sm">
      <div className="flex h-[100dvh] items-stretch justify-center p-0 sm:p-4">
        <div className="flex h-[100dvh] w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-[28px] sm:border sm:border-white/10 dark:sm:border-slate-700">
          {/* Header */}
          <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-purple-600 dark:text-purple-400">
                  Affectations
                </p>
                <h2 className="mt-1 text-xl font-black text-slate-900 dark:text-slate-100 sm:text-2xl">
                  {teacher?.prenom} {teacher?.nom}
                </h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                  Choisis les lieux puis les groupes à attribuer à cet enseignant.
                </p>
              </div>

              <button
                onClick={onClose}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            {errorMessage && (
              <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                {errorMessage}
              </div>
            )}

            <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
              {/* Resume */}
              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-300">
                  <FaUsersCog className="text-xl" />
                </div>

                <h3 className="mt-4 text-lg font-black text-slate-900 dark:text-slate-100">
                  Résumé
                </h3>

                <div className="mt-4 space-y-3">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Lieux sélectionnés
                    </p>
                    <p className="mt-2 text-3xl font-black text-slate-900 dark:text-slate-100">
                      {selectedDojos.length}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Groupes sélectionnés
                    </p>
                    <p className="mt-2 text-3xl font-black text-slate-900 dark:text-slate-100">
                      {totalSelectedGroups}
                    </p>
                  </div>
                </div>
              </div>

              {/* Selection */}
              <div className="space-y-4">
                <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <div className="mb-4 flex items-center gap-2">
                    <FaMapMarkerAlt className="text-blue-600 dark:text-blue-400" />
                    <h3 className="text-lg font-black text-slate-900 dark:text-slate-100">
                      Lieux
                    </h3>
                  </div>

                  {dojos.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {dojos.map((dojo) => {
                        const isSelected = selectedDojos.includes(String(dojo.id));

                        return (
                          <button
                            key={dojo.id}
                            type="button"
                            onClick={() => toggleDojoSelection(dojo.id)}
                            className={cn(
                              "rounded-2xl border px-4 py-2 text-sm font-semibold transition",
                              isSelected
                                ? "border-blue-600 bg-blue-600 text-white dark:border-blue-500 dark:bg-blue-500"
                                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                            )}
                          >
                            {dojo.nom_dojo}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Aucun lieu disponible.
                    </p>
                  )}
                </div>

                {selectedDojos.map((dojoId) => {
                  const dojo = dojos.find((d) => String(d.id) === String(dojoId));
                  const dojoGroups = groupsByDojo[String(dojoId)] || [];

                  return (
                    <div
                      key={dojoId}
                      className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900"
                    >
                      <div className="mb-4 flex items-center gap-2">
                        <FaLayerGroup className="text-purple-600 dark:text-purple-400" />
                        <h3 className="text-lg font-black text-slate-900 dark:text-slate-100">
                          {dojo?.nom_dojo || "Lieu"}
                        </h3>
                      </div>

                      {dojoGroups.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {dojoGroups.map((group) => {
                            const isSelected =
                              selectedGroupsByDojo[String(dojoId)]?.includes(group.id);

                            return (
                              <button
                                key={group.id}
                                type="button"
                                onClick={() => toggleGroupSelection(dojoId, group.id)}
                                className={cn(
                                  "rounded-2xl border px-4 py-2 text-sm font-semibold transition",
                                  isSelected
                                    ? "border-purple-600 bg-purple-600 text-white dark:border-purple-500 dark:bg-purple-500"
                                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                                )}
                              >
                                {group.nom_groupe}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Aucun groupe disponible pour ce lieu.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={demoMode || saving}
                className={cn(
                  "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-semibold text-white transition",
                  demoMode || saving
                    ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                    : "bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600"
                )}
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignTeacherModal;